import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Fuse from "fuse.js";
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  PageContent,
} from "beproduct-ui";
import { Download, FilePlus, Upload, FileBarChart, Trash2, Pencil, Lock, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/navigation/page-header";
import { readReportsSearch, reportsSearchValue, writeReportsSearch } from "@/components/navigation/reports-search-context";
import { isoSeconds } from "@/lib/format";

interface ReportRow {
  id: string;
  folderId: string | null;
  name: string;
  description: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string;
}

export const Route = createFileRoute("/_authed/$companySlug/reports/")({
  component: ReportsListPage,
});

function ReportsListPage() {
  const { company, isAdmin } = Route.useRouteContext();
  const { companySlug } = Route.useParams();
  const nav = useNavigate({ from: "/$companySlug/reports/" });
  const [searchState, setSearchState] = useState(() => ({
    companySlug,
    value: readReportsSearch(companySlug),
  }));
  const q = reportsSearchValue(searchState, companySlug);
  const setQ = (value: string) => {
    setSearchState({ companySlug, value });
    writeReportsSearch(companySlug, value);
  };

  const qc = useQueryClient();
  const [busy, setBusy] = useState<{ id: string; action: "delete" | "replace" | "edit" } | null>(null);
  const [editing, setEditing] = useState<ReportRow | null>(null);
  const [grantsFor, setGrantsFor] = useState<ReportRow | null>(null);

  // Hidden inputs drive the Upload + Replace flows. We click them
  // programmatically and read .files[0] on change.
  const uploadInput = useRef<HTMLInputElement>(null);
  const replaceInput = useRef<HTMLInputElement>(null);
  const replaceTargetRef = useRef<ReportRow | null>(null);

  const reportsBase = `/api/tenants/${encodeURIComponent(company.id)}/reports/reports`;

  const { data, isLoading, error } = useQuery({
    queryKey: ["reports", company.id],
    queryFn: () => api<ReportRow[]>(reportsBase),
  });

  async function deleteReport(row: ReportRow) {
    if (busy) return;
    if (!window.confirm(`Delete "${row.name}"? This cannot be undone.`)) return;
    setBusy({ id: row.id, action: "delete" });
    try {
      await api(`${reportsBase}/${row.id}`, { method: "DELETE" });
      qc.invalidateQueries({ queryKey: ["reports", company.id] });
    } catch (e) {
      window.alert(`Delete failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  async function onUploadFile(file: File | null) {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api(`${reportsBase}/upload`, { method: "POST", body: fd });
      qc.invalidateQueries({ queryKey: ["reports", company.id] });
    } catch (e) {
      window.alert(`Upload failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      if (uploadInput.current) uploadInput.current.value = "";
    }
  }

  async function onReplaceFile(file: File | null) {
    const target = replaceTargetRef.current;
    if (!file || !target) return;
    setBusy({ id: target.id, action: "replace" });
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api(`${reportsBase}/${target.id}/upload`, { method: "PUT", body: fd });
      qc.invalidateQueries({ queryKey: ["reports", company.id] });
    } catch (e) {
      window.alert(`Replace failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      replaceTargetRef.current = null;
      if (replaceInput.current) replaceInput.current.value = "";
      setBusy(null);
    }
  }

  function downloadReport(row: ReportRow) {
    // Navigate the browser straight at the download URL; the .NET
    // endpoint sets Content-Disposition so the browser handles "save as".
    // credentials: include via same-origin cookie.
    window.location.assign(`${reportsBase}/${row.id}/download`);
  }

  // Fuse fuzzy match on name + description — `'token` extended-search
  // semantics mirror the schema page's pattern.
  const fuse = useMemo(() => {
    if (!data) return null;
    return new Fuse(data, {
      keys: ["name", "description"],
      threshold: 0.4,
      ignoreLocation: true,
    });
  }, [data]);

  const visible = useMemo(() => {
    if (!data) return [];
    if (!q.trim() || !fuse) return data;
    return fuse.search(q).map((r) => r.item);
  }, [data, fuse, q]);

  return (
    <PageContent>
      <div className="pt-4 pb-8 md:pt-6 space-y-6">
        <PageHeader title="Reports" actions={isAdmin ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => uploadInput.current?.click()}
              >
                <Upload className="size-4" /> Upload .trdx
              </Button>
              <Button
                onClick={() =>
                  nav({ to: "/$companySlug/reports/new", params: { companySlug } })
                }
              >
                <FilePlus className="size-4" /> New report
              </Button>
            </div>
          ) : (
            <RefreshAccessButton onRefreshed={() => qc.invalidateQueries({ queryKey: ["reports", company.id] })} />
          )} />
        {/* Hidden file inputs — clicked programmatically by the
            Upload header button and per-row Replace icon. */}
        <input
          ref={uploadInput}
          type="file"
          accept=".trdx,application/xml,text/xml"
          className="hidden"
          onChange={(e) => onUploadFile(e.target.files?.[0] ?? null)}
        />
        <input
          ref={replaceInput}
          type="file"
          accept=".trdx,application/xml,text/xml"
          className="hidden"
          onChange={(e) => onReplaceFile(e.target.files?.[0] ?? null)}
        />

        <Input
          placeholder="Search reports…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-md"
        />

        {error ? (
          <Card>
            <CardContent className="p-6 text-destructive">Failed to load reports.</CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                  <tr className="border-b">
                    <th className="text-left p-3">Name</th>
                    <th className="text-left p-3">Description</th>
                    <th className="text-left p-3">Updated</th>
                    <th className="p-3 w-32 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading || !data ? (
                    <tr>
                      <td colSpan={4} className="text-center py-6 text-muted-foreground">
                        Loading…
                      </td>
                    </tr>
                  ) : visible.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center py-6 text-muted-foreground">
                        {q ? "No matches." : "No reports yet — click “New report” to create one."}
                      </td>
                    </tr>
                  ) : (
                    visible.map((r) => (
                      <tr
                        key={r.id}
                        className="border-b last:border-0 hover:bg-muted/50 cursor-pointer"
                        onClick={() =>
                          nav({
                            to: "/$companySlug/reports/$reportId",
                            params: { companySlug, reportId: r.id },
                          })
                        }
                      >
                        <td className="p-3">
                          <span className="inline-flex items-center gap-2">
                            <FileBarChart className="size-4 text-muted-foreground" />
                            {r.name}
                          </span>
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {r.description ?? "—"}
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {isoSeconds(r.updatedAt)}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadReport(r);
                            }}
                            title="Download .trdx"
                          >
                            <Download className="size-4 text-muted-foreground" />
                          </Button>
                          {isAdmin && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy?.id === r.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditing(r);
                                }}
                                title="Edit name and description"
                              >
                                <Pencil className="size-4 text-muted-foreground" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy?.id === r.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setGrantsFor(r);
                                }}
                                title="Manage role access"
                              >
                                <Lock className="size-4 text-muted-foreground" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy?.id === r.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  replaceTargetRef.current = r;
                                  replaceInput.current?.click();
                                }}
                                title="Replace with .trdx"
                              >
                                <Upload className="size-4 text-muted-foreground" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={busy?.id === r.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void deleteReport(r);
                                }}
                                title="Delete report"
                              >
                                <Trash2 className="size-4 text-muted-foreground" />
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
      <EditReportDialog
        report={editing}
        onClose={() => setEditing(null)}
        onSave={async (patch) => {
          if (!editing) return;
          setBusy({ id: editing.id, action: "edit" });
          try {
            await api(`${reportsBase}/${editing.id}`, {
              method: "PATCH",
              body: JSON.stringify(patch),
            });
            await qc.invalidateQueries({ queryKey: ["reports", company.id] });
            setEditing(null);
          } catch (e) {
            window.alert(`Save failed: ${e instanceof Error ? e.message : String(e)}`);
          } finally {
            setBusy(null);
          }
        }}
        saving={busy?.action === "edit"}
      />
      <GrantsDialog
        tenantId={company.id}
        report={grantsFor}
        onClose={() => setGrantsFor(null)}
      />
    </PageContent>
  );
}

function EditReportDialog({
  report,
  onClose,
  onSave,
  saving,
}: {
  report: ReportRow | null;
  onClose: () => void;
  onSave: (patch: { name: string; description: string | null }) => void | Promise<void>;
  saving: boolean;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  // Seed form when the dialog opens for a new row.
  useEffect(() => {
    if (report) {
      setName(report.name);
      setDescription(report.description ?? "");
    }
  }, [report]);

  const open = report !== null;
  const trimmedName = name.trim();
  const canSave =
    !!report &&
    !saving &&
    trimmedName.length > 0 &&
    (trimmedName !== report.name ||
      (description.trim() || null) !== (report.description ?? null));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit report</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="rpt-name">Name</label>
            <Input
              id="rpt-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Report name"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground" htmlFor="rpt-desc">Description</label>
            <Input
              id="rpt-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            disabled={!canSave}
            onClick={() => onSave({
              name: trimmedName,
              description: description.trim() ? description.trim() : null,
            })}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RoleRow { id: string; name: string; isAdmin: boolean }

/**
 * Per-report role-grant editor. Opens when the user clicks the Lock
 * icon on a row. Fetches the tenant's cached role list (from the
 * Phase-1 sync) and the current grants for this report, then lets
 * an admin tick/untick which roles can VIEW the report. PUT replaces
 * the full set in one call. Mutations are admin-only (gated server-
 * side), but we don't even render the trigger button unless isAdmin.
 *
 * Empty cached roles → prompt the user to press "Refresh roles" in
 * the company settings first, since granting a role we haven't cached
 * upstream would never resolve to anyone.
 */
function GrantsDialog({
  tenantId,
  report,
  onClose,
}: {
  tenantId: string;
  report: ReportRow | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const open = report !== null;

  const rolesQ = useQuery({
    queryKey: ["tenant-roles", tenantId],
    queryFn: () => api<{ roles: RoleRow[]; lastSyncedAt: string | null }>(
      `/api/tenants/${tenantId}/roles`,
    ),
    enabled: open,
  });

  const [refreshing, setRefreshing] = useState(false);
  async function refreshRoles(): Promise<void> {
    setRefreshing(true);
    try {
      await api(`/api/tenants/${tenantId}/roles/sync`, { method: "POST" });
      qc.invalidateQueries({ queryKey: ["tenant-roles", tenantId] });
    } catch (e) {
      window.alert(
        `Refresh failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setRefreshing(false);
    }
  }
  const grantsQ = useQuery({
    queryKey: ["report-grants", tenantId, report?.id ?? null],
    queryFn: () => api<{ roleIds: string[] }>(
      `/api/tenants/${tenantId}/report-grants/${report!.id}`,
    ),
    enabled: open && report !== null,
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // Seed the selection when the grants query first lands for this row.
  useEffect(() => {
    if (grantsQ.data) setSelected(new Set(grantsQ.data.roleIds));
  }, [grantsQ.data]);

  function toggle(roleId: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  }

  async function save(): Promise<void> {
    if (!report) return;
    setSaving(true);
    try {
      await api(`/api/tenants/${tenantId}/report-grants/${report.id}`, {
        method: "PUT",
        body: JSON.stringify({ roleIds: [...selected] }),
      });
      qc.invalidateQueries({
        queryKey: ["report-grants", tenantId, report.id],
      });
      onClose();
    } catch (e) {
      window.alert(
        `Save failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setSaving(false);
    }
  }

  const roles = rolesQ.data?.roles ?? [];
  const noRolesCached = !rolesQ.isLoading && roles.length === 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Access · {report?.name ?? ""}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <p className="text-muted-foreground">
            Roles checked below can <strong>view</strong> this report. Admins
            always see every report regardless of grants. Edit / upload /
            delete remain admin-only.
          </p>
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground">
              {rolesQ.data?.lastSyncedAt
                ? `Roles last refreshed ${new Date(rolesQ.data.lastSyncedAt).toLocaleString()}`
                : "Roles not yet synced from BeProduct"}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void refreshRoles()}
              disabled={refreshing}
              title="Re-pull the BeProduct role list for this tenant"
            >
              <RefreshCw className={"size-4 " + (refreshing ? "animate-spin" : "")} />
              {refreshing ? "Refreshing…" : "Refresh roles"}
            </Button>
          </div>
          {rolesQ.isLoading || grantsQ.isLoading ? (
            <div className="text-muted-foreground">Loading…</div>
          ) : noRolesCached ? (
            <div className="rounded-md border bg-muted/30 px-3 py-2 text-muted-foreground">
              No roles cached yet. Press <strong>Refresh roles</strong> above
              to pull the list from BeProduct.
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto rounded-md border divide-y">
              {roles.map((r) => {
                const checked = selected.has(r.id);
                return (
                  <label
                    key={r.id}
                    className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(r.id)}
                    />
                    <span className="flex-1">{r.name}</span>
                    {r.isAdmin && (
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                        admin
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            disabled={saving || rolesQ.isLoading || grantsQ.isLoading || noRolesCached}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Non-admin affordance: re-resolve the caller's upstream BeProduct
 * role for every tenant and refetch the reports list. Useful when an
 * admin grants them access to a new report (or a new role upstream)
 * and they don't want to log out / back in to pick it up.
 *
 * The server endpoint (POST /api/auth/me/roles/refresh) updates
 * dataflow.user_oidc_grants.tenant_roles. The next reports list call
 * the user makes will then carry their freshly-resolved role into the
 * proxy's grant lookup.
 */
function RefreshAccessButton({ onRefreshed }: { onRefreshed: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await api("/api/auth/me/roles/refresh", { method: "POST" });
          onRefreshed();
        } catch (e) {
          window.alert(
            `Refresh failed: ${e instanceof Error ? e.message : String(e)}`,
          );
        } finally {
          setBusy(false);
        }
      }}
      title="Re-check role grants if an admin just gave you access to a new report"
    >
      <RefreshCw className={"size-4 " + (busy ? "animate-spin" : "")} />
      {busy ? "Refreshing…" : "Refresh my access"}
    </Button>
  );
}
